import { useEffect, useState, useMemo } from 'react'
import { actions as appActions } from 'src/features/state'
import { actions } from '../../RestockingState'
import { useDispatch } from 'react-redux'
import { useSelector } from 'src/utils/store'
import { defaultFulfillmentCenterTypeFilters } from 'src/features/fulfillmentCenterFilters'
import { selectFulfillmentCenters } from 'src/features/selector'
import * as S from './RestockingLabelPrint.styles'
import { IconButton, Tooltip, Typography } from '@mui/material'
import PrintIcon from '@mui/icons-material/Print'
import MultipleSelect from 'src/components/MultipleSelect/MultipleSelect'
import { useForm } from 'react-hook-form'
import PrinterInfo from 'src/features/fraction/components/PrinterInfo'
import TableMui from 'src/components/TableMui'
import { toast } from 'react-toastify'
import { OpenInNew } from '@mui/icons-material'
import { LocationTypeEnum } from '../../RestockingTypes'

interface Filter {
  addressType: number[];
  fc: number[];
  availability: string[];
}

interface SelectedAddressToPrint {
  id: number
  address: string
}

export const RestockingLabelPrint = () => {
  const dispatch = useDispatch()

  const fulfillmentCenters = useSelector(selectFulfillmentCenters)

  useEffect(() => {
    dispatch(appActions.getAvailableFulfillmentCenters(defaultFulfillmentCenterTypeFilters))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fcOptions = useMemo(() => fulfillmentCenters.map((fulfillmentCenter) => ({
    id: fulfillmentCenter.id,
    description: fulfillmentCenter.name,
  })), [fulfillmentCenters])

  const availabilityOptions = useMemo(() => [
    { id: 1, description: 'Disponível' },
    { id: 2, description: 'Indisponível' },
  ], [])
  const addressTypeOptions = useMemo(() => [
    { id: LocationTypeEnum.MOVEMENT, description: 'Movimentações' },
    { id: LocationTypeEnum.TRANSFER_CAGE, description: 'Transferências' },
  ], [])
  const movingAddresses = useSelector((state) => state.restocking.movingAddresses)

  const [selectedAddressesToPrint, setSelectedAddressesToPrint] = useState<SelectedAddressToPrint[]>([])
  const [preventDoubleClick, setPreventDoubleClick] = useState(false)

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<Filter>({
    defaultValues: { addressType: [6], fc: [], availability: [] },
  })

  const handlePrintAddresses = (addresses: string[]) => {
    if (preventDoubleClick) {
      return
    }

    setPreventDoubleClick(true)

    const onFinally = () => {
      setPreventDoubleClick(false)
    }

    dispatch(actions.printAddressesLabel({
      addresses,
      onFinally,
    }))
  }

  const handleOpenPageDetails = (idLocationType: number, idPage: number | null) => {
    if (idPage === null) {
      return
    }

    if (idLocationType === LocationTypeEnum.TRANSFER_CAGE) {
      window.open(`/transfers/manage/page/${idPage}/details`, '_blank')
    } else if (idLocationType === LocationTypeEnum.MOVEMENT) {
      window.open(`/restocking/manage/page/${idPage}/details`, '_blank')
    }

    window.focus()
  }

  const resolveTableRows = useMemo(() => movingAddresses.map((data) => ({
    id: data.idLocation,
    fc: data.fc,
    address: data.address,
    hasStock: data.hasStock ? 'SIM' : 'NÃO',
    blocked: data.isBlocked ? 'SIM' : 'NÃO',
    available: data.isAvailable ? 'SIM' : 'NÃO',
    listTypeName: data.listTypeName || '-',
    userName: data.userName || '-',
    usingTime: data.usingTime || '-',
    action: (
      <S.TableColumnActions>
        <Tooltip
          sx={{ cursor: 'pointer' }}
          title='Imprimir endereço'
          placement='top'
          arrow
          data-testid='print-tooltip-test-id'
        >
          <IconButton
            onClick={() => handlePrintAddresses([data.address])}
            style={{
              padding: 0, color: preventDoubleClick ? '#5c5c5c' : '#1976d2', cursor: 'pointer',
            }}
            disabled={preventDoubleClick}
            data-testid='print-icon-button-test-id'
          >
            <PrintIcon style={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
        <Tooltip
          sx={{ cursor: 'pointer' }}
          title='Abrir detalhes da página'
          placement='top'
          arrow
          data-testid='open-page-details-tooltip-test-id'
        >
          <IconButton
            onClick={() => handleOpenPageDetails(data.idLocationType, data.idPage)}
            style={{
              padding: 0, color: preventDoubleClick || data.idPage === null ? '#5c5c5c' : '#1976d2', cursor: 'pointer',
            }}
            disabled={preventDoubleClick || data.idPage === null}
            data-testid='open-page-details-icon-button-test-id'
          >
            <OpenInNew style={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </S.TableColumnActions>
    ),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  })), [movingAddresses, preventDoubleClick])

  const submitSearchMovingAddress = (filters: Filter) => {
    const { addressType, fc } = filters
    const availability = filters.availability.length === 2 || filters.availability.length === 0 ? 'both' : filters.availability[0] === 'Disponível' ? 'yes' : 'no'

    dispatch(actions.fetchMovingAddress({
      addressType,
      fc,
      availability,
    }))
  }

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const selectedAddresses = resolveTableRows.map((row) => ({ id: row.id, address: row.address }))
      setSelectedAddressesToPrint(selectedAddresses)
      return
    }
    setSelectedAddressesToPrint([])
  }

  const handleCheckboxClick = (id: number) => {
    const selectedIndex = selectedAddressesToPrint.findIndex((selectedAddressToPrint) => selectedAddressToPrint.id === id)

    if (selectedIndex === -1) {
      const movingAddress = movingAddresses.find((x) => x.idLocation === id)

      if (movingAddress) {
        setSelectedAddressesToPrint([...selectedAddressesToPrint, { id: movingAddress.idLocation, address: movingAddress.address }])
      }
    } else {
      const updatedSelectedIds = selectedAddressesToPrint.filter((selectedAddressToPrint) => selectedAddressToPrint.id !== id)
      setSelectedAddressesToPrint(updatedSelectedIds)
    }
  }

  const handlePrintSelectedAddresses = () => {
    if (selectedAddressesToPrint.length === 0) {
      return
    }

    if (selectedAddressesToPrint.length > 10) {
      toast.error('Limite máximo de 10 impressões por vez.')
      return
    }

    const addressesToPrint = selectedAddressesToPrint.map((item) => item.address)

    handlePrintAddresses(addressesToPrint)
  }

  return (
    <S.Wrapper>
      <S.HeaderContainer>
        <Typography variant='h5' fontWeight='bold'>
          Impressão de Etiquetas
        </Typography>
        <PrinterInfo />
      </S.HeaderContainer>
      <S.FormContainer onSubmit={handleSubmit(submitSearchMovingAddress)}>
        <S.FiltersContainer>
          <MultipleSelect
            register={register}
            inputName='addressType'
            label='Tipo de Endereço'
            options={addressTypeOptions}
            optionProperty='id'
            width='20rem'
            errors={errors.addressType}
            dataTestId='address-type-test-id'
            required
            defaultOptions={getValues('addressType')}
          />
          <MultipleSelect
            register={register}
            inputName='fc'
            label='FC'
            options={fcOptions}
            optionProperty='id'
            width='20rem'
            errors={errors.fc}
            dataTestId='fc-test-id'
          />
          <MultipleSelect
            register={register}
            inputName='availability'
            label='Disponibilidade'
            options={availabilityOptions}
            width='20rem'
            errors={errors.availability}
            dataTestId='availability-test-id'
          />
        </S.FiltersContainer>
        <S.CustomButton
          title='Buscar'
          id='searchButton'
          bgColor='lightBlue3'
          hoverColor='darkBlueHover2'
          letterColor='white'
          testId='search-button-test-id'
          type='submit'
        />
      </S.FormContainer>
      <TableMui
        columns={[
          { id: 'fc', label: 'FC' },
          { id: 'address', label: 'Endereço' },
          { id: 'hasStock', label: 'Tem estoque?' },
          { id: 'blocked', label: 'Bloqueado?' },
          { id: 'available', label: 'Disponível?' },
          { id: 'listTypeName', label: 'Tipo de lista' },
          { id: 'userName', label: 'Keeper' },
          { id: 'usingTime', label: 'Tempo de uso' },
          { id: 'action', label: 'Ação', useSorting: false },
        ]}
        emptyLabel='Nenhum registro encontrado'
        rows={resolveTableRows}
        rowCount={resolveTableRows.length}
        rowsPerPageLimit={10}
        enablePagination
        maxHeight='60vh'
        hasSorting
        hasCheckbox
        selected={selectedAddressesToPrint.map((item) => item.id)}
        selectedCount={selectedAddressesToPrint.length}
        onRowCheckboxClick={handleCheckboxClick}
        onSelectAllClick={handleSelectAllClick}
        testId='restocking-label-print-table-test-id'
        tableFooter={(
          <S.TableFooterContainer>
            <Typography variant='body1'>
              Endereço(s) selecionado(s): {selectedAddressesToPrint.length}
            </Typography>
            <S.CustomButton
              title='Imprimir Selecionados'
              id='printButton'
              bgColor='blue'
              hoverColor='darkBlueHover'
              letterColor='white'
              testId='print-button-test-id'
              type='button'
              disabled={selectedAddressesToPrint.length === 0 || preventDoubleClick}
              onClick={handlePrintSelectedAddresses}
            />
          </S.TableFooterContainer>
        )}
      />
    </S.Wrapper>
  )
}
